import { Card, KeyValueList, Link, Text, useTectonTheme } from '@lithos';
import React, { FC } from 'react';
import {
  EntityFCO,
  FCOFields,
  FeatureServiceFCO,
  FeatureServiceFCOFields,
  FeatureViewFCO,
  FeatureViewFCOFields,
  WorkspaceFCOContainer,
} from '../../../core/types/fcoTypes';
import { useUserSettings } from '../../context/UserSettingsContext';

import { FeatureServiceSummary } from '../../../core/types/featureServiceSummary';
import { EnabledDisabledBadge } from '../../@tecton/ComponentRedesign';

interface FeatureServiceConfigCardProps {
  fco: FeatureServiceFCO;
  fcos: WorkspaceFCOContainer;
  summary: FeatureServiceSummary;
}

const FeatureServiceConfigCard: FC<FeatureServiceConfigCardProps> = ({ fco, fcos, summary }) => {
  const { theme } = useTectonTheme();
  const { workspace } = useUserSettings();
  const dependentEntityIds = fcos.featureViews
    .filter((featureView) => fco[FeatureServiceFCOFields.ALL_FEATURE_VIEWS].includes(featureView[FCOFields.ID]))
    .map((featureView: FeatureViewFCO) => {
      return featureView[FeatureViewFCOFields.ALL_ENTITIES];
    })
    .flat();

  const dependentEntities = fcos.entities.filter((entity: EntityFCO) => {
    return dependentEntityIds.includes(entity[FCOFields.ID]);
  });

  const dependentFVJoinKeys = fcos.featureViews
    .filter((featureView) => fco[FeatureServiceFCOFields.ALL_FEATURE_VIEWS].includes(featureView[FCOFields.ID]))
    .map((featureView: FeatureViewFCO) => featureView[FeatureViewFCOFields.JOIN_KEYS])
    .flat();

  // Feature Service which has "Realtime" dependant FVs can have onlineJoinKeys which we need to show if they have it.
  const onlineJoinKeys = summary?.onlineJoinKeys?.split(',') ?? [];
  const trimmedOnlineJoinKeys = onlineJoinKeys.map((key) => key.trim());

  const dependentJoinKeyAndOnlineJoinKeys = [...trimmedOnlineJoinKeys, ...dependentFVJoinKeys].flat();
  const noDuplicateJoinKeys = Array.from(new Set(dependentJoinKeyAndOnlineJoinKeys));
  const joinKeysList = noDuplicateJoinKeys.join(', ');

  const list = [
    {
      title: <>Entities</>,
      content: (
        <div style={{ display: 'flex', flexWrap: 'wrap' }}>
          {dependentEntities.length === 0 && <>n/a</>}
          {dependentEntities.map((entity: EntityFCO, index: number) => {
            return (
              <>
                <Link
                  name={entity.name}
                  icon="Entities"
                  url={`/repo/${workspace}/entities/${entity[FCOFields.NAME]}/overview`}
                />

                {index + 1 !== dependentEntities.length && <>,&nbsp;&nbsp;&nbsp;&nbsp;</>}
              </>
            );
          })}
        </div>
      ),
    },
    {
      title: <>Join Keys</>,
      content: dependentJoinKeyAndOnlineJoinKeys.length === 0 ? <>n/a</> : <Text code>{joinKeysList}</Text>,
    },
    {
      title: <>Request Data Source Keys</>,
      content: !summary?.requestContextKeys ? <>n/a</> : <Text code>{summary?.requestContextKeys}</Text>,
    },
    {
      title: <>Online Serving</>,
      content: <EnabledDisabledBadge enabled={fco[FeatureServiceFCOFields.IS_ONLINE_SERVING_ENABLED]} />,
    },
    {
      title: <>On-Demand Environment</>,
      content: fco[FeatureServiceFCOFields.REALTIME_ENVIRONMENT] ? (
        <Text code>{fco[FeatureServiceFCOFields.REALTIME_ENVIRONMENT]}</Text>
      ) : (
        <>n/a</>
      ),
    },
    {
      title: <>Logging Enabled</>,
      content: <EnabledDisabledBadge enabled={summary?.isLoggingEnabled} />,
    },
    {
      title: <>Logging Sample Rate</>,
      content: !summary?.loggingSampleRate ? <>n/a</> : <Text code>{summary?.loggingSampleRate}</Text>,
    },
    {
      title: <>Log Effective Times</>,
      content: !summary?.logEffectiveTimes ? <>n/a</> : <EnabledDisabledBadge enabled={summary?.logEffectiveTimes} />,
    },
    {
      title: <>Caching</>,
      content: <EnabledDisabledBadge enabled={fco[FeatureServiceFCOFields.IS_CACHING_ENABLED]} />,
    },
  ];

  return (
    <Card title="Configuration">
      <KeyValueList items={list} />
    </Card>
  );
};

export default FeatureServiceConfigCard;
